/**
 * @file relative-calendar.ts
 * @description Relative target date and target specification schemas for CountTheTime.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

import { z } from 'zod';

/*======================================================================
 * Configuration Defaults & Constants
 *======================================================================*/

/**
 * Default local wall-clock target time (midnight).
 */
export const DEFAULT_TARGET_TIME = '00:00:00';

/**
 * Regex matching 24-hour wall-clock time in HH:MM:SS or HH:MM format.
 */
export const WALL_CLOCK_TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;

/*======================================================================
 * Helper Validators
 *======================================================================*/

/**
 * Validates whether a string is a valid IANA timezone identifier.
 *
 * @param timezone - The timezone identifier string to validate.
 * @returns True if valid in the runtime Intl environment.
 */
export const isValidIanaTimezone = (timezone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/*======================================================================
 * Relative Target Specification Schema
 *======================================================================*/

/**
 * Schema validating relative target rule identifiers.
 * Accepts any non-empty string identifier provided by registered calendar or astronomical plugins.
 */
export const RelativeRuleSchema = z.string().min(1, 'Rule identifier cannot be empty');

/**
 * Alias for RelativeRuleSchema.
 */
export const RelativeCalendarRuleSchema = RelativeRuleSchema;

/**
 * Extensible rule identifier type for relative target date resolution.
 */
export type RelativeRule = z.infer<typeof RelativeRuleSchema>;

/**
 * Alias for RelativeRule type.
 */
export type RelativeCalendarRule = RelativeRule;

/**
 * Schema validating dynamic relative target specifications.
 */
export const RelativeTargetSpecSchema = z.object({
  /**
   * Target specification discriminator.
   */
  type: z.literal('RELATIVE'),

  /**
   * Relative target rule identifier resolved by registered plugins (e.g. 'THANKSGIVING_US', 'EASTER_SUNDAY', 'NEXT_SUNRISE', 'FIXED_DATE').
   */
  rule: RelativeRuleSchema,

  /**
   * Optional parameters passed to the rule plugin (e.g., { month: 11, weekday: 4, nth: 4 }).
   */
  params: z.record(z.string(), z.unknown()).optional(),

  /**
   * Local wall-clock target time in 'HH:MM:SS' or 'HH:MM' format.
   * @defaultValue {@link DEFAULT_TARGET_TIME} ('00:00:00')
   */
  targetTime: z
    .string()
    .regex(WALL_CLOCK_TIME_REGEX, {
      message: 'Invalid targetTime format. Expected HH:MM:SS or HH:MM',
    })
    .default(DEFAULT_TARGET_TIME),

  /**
   * Optional IANA timezone identifier overriding the parent timer timezone.
   */
  timezone: z.string().refine(isValidIanaTimezone, { message: 'Invalid IANA timezone' }).optional(),

  /**
   * Optional active celebration grace period duration (ISO 8601 string e.g. 'P1D' or numeric ms).
   */
  periodicActiveDuration: z.union([z.string(), z.number()]).optional(),

  /**
   * Optional contextual status label for active event grace periods (e.g. 'Happening now', 'In progress').
   */
  duringLabel: z.string().optional(),

  /**
   * Optional contextual status label for active event grace periods (e.g. 'LIVE NOW', 'IN PROGRESS').
   */
  activeLabel: z.string().optional(),

  /**
   * Optional day offset relative to the resolved event date (e.g. -1 for the day before, +2 for two days after).
   */
  offsetDays: z.number().int().min(-365).max(365).optional(),
});

/**
 * Dynamic relative target event specification.
 */
export type RelativeTargetSpec = z.infer<typeof RelativeTargetSpecSchema>;

/**
 * Alias for RelativeTargetSpecSchema.
 */
export const RelativeCalendarSpecSchema = RelativeTargetSpecSchema;

/**
 * Alias for RelativeTargetSpec type.
 */
export type RelativeCalendarSpec = RelativeTargetSpec;

/*======================================================================
 * Target Specification Schemas & Discriminated Union
 *======================================================================*/

/**
 * Schema validating static instant target specifications.
 */
export const InstantTargetSpecSchema = z.object({
  /**
   * Target specification discriminator.
   */
  type: z.literal('INSTANT'),

  /**
   * Optional target anchor ISO 8601 date-time string.
   */
  anchorDate: z.iso.datetime().optional(),
});

/**
 * Static instant target specification.
 */
export type InstantTargetSpec = z.infer<typeof InstantTargetSpecSchema>;

/**
 * Schema validating relative duration target specifications.
 */
export const DurationTargetSpecSchema = z.object({
  /**
   * Target specification discriminator.
   */
  type: z.literal('DURATION'),

  /**
   * ISO 8601 duration string (e.g., 'PT15M').
   */
  duration: z.string().optional(),

  /**
   * Duration in milliseconds relative to activation.
   */
  durationMs: z.number().nonnegative().optional(),
});

/**
 * Relative duration target specification.
 */
export type DurationTargetSpec = z.infer<typeof DurationTargetSpecSchema>;

/**
 * Schema validating recurrence frequencies.
 */
export const RecurrenceFrequencySchema = z.enum(['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM']);

/**
 * Supported recurrence frequencies.
 */
export type RecurrenceFrequency = z.infer<typeof RecurrenceFrequencySchema>;

/**
 * Schema validating custom recurrence interval units.
 */
export const RecurringIntervalUnitSchema = z.enum(['hours', 'days', 'weeks']);

/**
 * Supported custom recurrence interval units.
 */
export type RecurringIntervalUnit = z.infer<typeof RecurringIntervalUnitSchema>;

/**
 * Schema validating visual recurrence specifications for PERIODIC repeating schedules.
 */
export const RecurringTargetSpecSchema = z.object({
  /**
   * Target specification discriminator.
   */
  type: z.literal('RECURRING'),

  /**
   * Recurrence frequency classification.
   */
  frequency: RecurrenceFrequencySchema,

  /**
   * Local wall-clock target time in 'HH:MM:SS' or 'HH:MM' format.
   * @defaultValue {@link DEFAULT_TARGET_TIME} ('00:00:00')
   */
  targetTime: z
    .string()
    .regex(WALL_CLOCK_TIME_REGEX, {
      message: 'Invalid targetTime format. Expected HH:MM:SS or HH:MM',
    })
    .default(DEFAULT_TARGET_TIME),

  /**
   * Days of the week on which the event repeats (0 = Sunday, 1 = Monday, ..., 6 = Saturday).
   */
  daysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),

  /**
   * Day of the month on which the event repeats (1–31).
   */
  dayOfMonth: z.number().int().min(1).max(31).optional(),

  /**
   * Custom recurrence interval multiplier (e.g. 2 for every 2 weeks).
   */
  interval: z.number().int().positive().optional(),

  /**
   * Custom recurrence interval unit.
   */
  intervalUnit: RecurringIntervalUnitSchema.optional(),

  /**
   * Optional initial anchor date timestamp serving as baseline for custom interval stepping.
   */
  anchorDate: z.iso.datetime().optional(),

  /**
   * Optional IANA timezone identifier overriding the parent timer timezone.
   */
  timezone: z.string().refine(isValidIanaTimezone, { message: 'Invalid IANA timezone' }).optional(),

  /**
   * Optional active celebration grace period duration (ISO 8601 string or numeric ms).
   */
  periodicActiveDuration: z.union([z.string(), z.number()]).optional(),

  /**
   * Optional contextual status label for active event grace periods (e.g. 'Happening now', 'In progress').
   */
  duringLabel: z.string().optional(),

  /**
   * Optional contextual status label for active event grace periods (e.g. 'LIVE NOW', 'IN PROGRESS').
   */
  activeLabel: z.string().optional(),
});

/**
 * Visual recurrence specification for repeating schedules.
 */
export type RecurringTargetSpec = z.infer<typeof RecurringTargetSpecSchema>;

/**
 * Input format for a visual recurrence specification where targetTime is optional.
 */
export type RecurringTargetInput = z.input<typeof RecurringTargetSpecSchema>;

/**
 * Discriminated union schema across all supported target specification types.
 */
export const TargetSpecSchema = z.discriminatedUnion('type', [
  InstantTargetSpecSchema,
  RelativeTargetSpecSchema,
  DurationTargetSpecSchema,
  RecurringTargetSpecSchema,
]);

/**
 * Unified target event specification type.
 */
export type TargetSpec = z.infer<typeof TargetSpecSchema>;
